/**
 * PatternsSection - Pattern spotlight for L2+ users
 *
 * Extracted from ModernResultsFull.tsx for Phase 3 Mobile Optimization.
 * Tier-gated: Only visible for tierLevel >= 2 (Drama+, DramaX)
 */

import { View, Pressable, StyleSheet } from 'react-native';
import { TierBody, TierHeadline } from '@dramadetector/ui';
import type { Pattern } from '@dramadetector/shared';

import { captureEvent } from '../../../lib/analytics';
import { useSectionTruncation, TRUNCATION_DEFAULTS } from '../hooks';
import { sharedStyles } from '../sharedStyles';
import { usePatternSpotlightFlag } from '../../../hooks/useFeatureFlags';

interface PatternCardProps {
  pattern: Pattern;
  t: (key: string, options?: Record<string, unknown>) => string;
}

/**
 * Individual pattern card display
 */
function PatternCard({ pattern, t }: PatternCardProps) {
  return (
    <View style={sharedStyles.featureCard}>
      {/* Tier Badge */}
      <View style={sharedStyles.tierBadge}>
        <TierBody size="xs" style={{ color: '#5B21B6', fontWeight: '600' }}>
          {t('common.tier_drama_plus', { defaultValue: 'Drama+' })}
        </TierBody>
      </View>

      {/* Pattern Type */}
      <TierBody size="sm" color="text-gray-800" style={{ fontWeight: '600', marginBottom: 4 }}>
        {(pattern.type || 'Pattern').replace(/([A-Z])/g, ' $1').trim()}
      </TierBody>

      {/* Description */}
      <TierBody size="sm" color="text-gray-600" style={{ marginBottom: 8 }}>
        {pattern.description}
      </TierBody>

      {/* Frequency */}
      <TierBody size="xs" color="text-purple-600">
        {t('home.modern.found_times', {
          defaultValue: 'Found {count} time(s)',
          count: pattern.frequency,
        })}
      </TierBody>

      {/* Examples */}
      {pattern.examples && pattern.examples.length > 0 && (
        <View style={[sharedStyles.snippetBox, { marginTop: 8 }]}>
          <TierBody size="xs" color="text-gray-500">
            {pattern.examples.slice(0, 2).map((ex) => `"${ex}"`).join(', ')}
          </TierBody>
        </View>
      )}
    </View>
  );
}

export interface PatternsSectionProps {
  /** List of patterns to display */
  patterns: Pattern[];
  /** Translation function */
  t: (key: string, options?: Record<string, unknown>) => string;
  /** Test ID for testing */
  testID?: string;
  /** Maximum items before truncation (default: 3) */
  maxVisible?: number;
}

/**
 * Patterns section with header and list (L2+ only)
 * Supports truncation with "Show more/less" toggle
 */
export function PatternsSection({
  patterns,
  t,
  testID,
  maxVisible = TRUNCATION_DEFAULTS.patterns,
}: PatternsSectionProps) {
  const isEnabled = usePatternSpotlightFlag();
  const { showAll, toggle, isTruncated, visibleCount, hiddenCount } = useSectionTruncation({
    totalItems: patterns.length,
    maxVisible,
  });

  if (isEnabled === false) return null;

  if (!patterns || patterns.length === 0) {
    return null;
  }

  const visiblePatterns = showAll ? patterns : patterns.slice(0, visibleCount);

  const handleToggle = () => {
    captureEvent('Section_Toggle', {
      section: 'patterns',
      action: showAll ? 'collapse' : 'expand',
      total_items: patterns.length,
      visible_items: visibleCount,
    });
    toggle();
  };

  return (
    <View testID={testID}>
      <View style={[sharedStyles.sectionHeader, { maxWidth: 400, alignSelf: 'center', width: '100%' }]}>
        <TierHeadline size="lg" weight="semibold" color="text-white" as="h3">
          {t('home.modern.pattern_spotlight', { defaultValue: 'Pattern Spotlight' })}
        </TierHeadline>
      </View>
      {visiblePatterns.map((pattern, index) => (
        <PatternCard key={`pattern-${index}`} pattern={pattern} t={t} />
      ))}
      {/* Show more/less toggle */}
      {isTruncated && (
        <Pressable
          onPress={handleToggle}
          style={styles.toggleButton}
          accessibilityRole="button"
          accessibilityLabel={
            showAll
              ? t('home.modern.show_less', { defaultValue: 'Show less' })
              : t('home.modern.show_more', { defaultValue: 'Show {count} more', count: hiddenCount })
          }
          testID={`${testID}-toggle`}
        >
          <TierBody size="sm" style={styles.toggleText}>
            {showAll
              ? t('home.modern.show_less', { defaultValue: 'Show less' })
              : t('home.modern.show_more', { defaultValue: 'Show {count} more', count: hiddenCount })}
          </TierBody>
        </Pressable>
      )}
    </View>
  );
}

/**
 * StyleSheet - WCAG AAA compliant
 * - Touch target: min 44x44 points
 * - Contrast: purple-800 #5B21B6 on white = 9.4:1
 */
const styles = StyleSheet.create({
  toggleButton: {
    paddingVertical: 14,
    minHeight: 44, // WCAG touch target
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleText: {
    color: '#5B21B6', // purple-800 (WCAG AAA: 9.4:1 contrast)
    fontWeight: '600',
  },
});
